import sentryFetch from "@amzn/sentry-fetch";

/** Whether `url` is a call to the Lusca Search API at `apiHost`. */
export function isLuscaApiUrl(
  url: string | undefined,
  apiHost: string | undefined
): boolean {
  if (!url || !apiHost) return false;
  try {
    return new URL(url).host === apiHost;
  } catch {
    return false;
  }
}

/**
 * Signs the browser in to the API's MidwayAuthorizer, so the next request carries its session
 * cookie.
 *
 * Without a session MidwayAuthorizer answers with a redirect to Midway that has no CORS headers,
 * which the browser reports as a network error. SentryFetch completes that sign-in, showing the
 * Midway prompt if the user has no Midway session.
 */
export async function establishMidwayAuthorizerSession(
  apiHost: string
): Promise<boolean> {
  try {
    const response = await sentryFetch(`https://${apiHost}/jwt/permissions`, {
      credentials: "include",
      sentryOptions: { followMidwayStepUp: true },
    });
    return response.ok;
  } catch {
    return false;
  }
}

const SAFE_METHODS = new Set(["get", "head", "options"]);
const TIMEOUT_CODES = new Set(["ECONNABORTED", "ETIMEDOUT"]);

/**
 * Whether a request that failed with no response may be sent again after signing in.
 *
 * The server may already have handled it: a timeout, or an error response without CORS headers,
 * also fails with no response. So only requests that are safe to repeat are sent again, and a
 * timeout is not taken for a sign-in redirect.
 */
export function canResendAfterSignIn(
  method: string | undefined,
  headers: Record<string, unknown> | undefined,
  errorCode: string | undefined
): boolean {
  if (errorCode && TIMEOUT_CODES.has(errorCode)) return false;
  return (
    SAFE_METHODS.has((method ?? "get").toLowerCase()) ||
    Boolean(headers?.["Idempotency-Key"])
  );
}
